import React, { useState, useEffect } from 'react';
import { Product } from '../types';
import { useNavigate } from 'react-router-dom';
import { getImageUrl, imageExists } from '../utils/imageUtils';
import styles from './ProductCard.module.css';

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className: string;
  onClick?: () => void;
}

const ImageWithFallback: React.FC<ImageWithFallbackProps> = ({ 
  src, 
  alt, 
  className, 
  onClick 
}) => {
  const [imgSrc, setImgSrc] = useState(getImageUrl(src));
  const [errored, setErrored] = useState(false);

  // Check if image exists when component mounts or src changes
  useEffect(() => {
    let isMounted = true;
    
    const checkImage = async () => {
      const exists = await imageExists(imgSrc);
      if (!exists && isMounted) {
        setImgSrc('/images/placeholder.png');
        setErrored(true);
      }
    };

    if (!errored) {
      checkImage();
    }

    return () => {
      isMounted = false;
    };
  }, [src, imgSrc, errored]);

  const handleError = () => {
    if (!errored) {
      setImgSrc('/images/placeholder.png');
      setErrored(true);
    }
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      onError={handleError}
      onClick={onClick}
    />
  );
};

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.productCard}>
      <ImageWithFallback
        src={getImageUrl(product.image || product.imageUrl)}
        alt={product.name}
        className={styles.productImage}
        onClick={() => navigate(`/product/${product.id}`)}
      />
      <h3
        className={styles.productName}
        onClick={() => navigate(`/product/${product.id}`)}
      >
        {product.name}
      </h3>
      <p className={styles.productPrice}>
        {product.price ? `$${product.price.toFixed(2)}` : 'Price not available'}
      </p>
    </div>
  );
};

export default ProductCard;
